import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSliders,
  FiEyeOff,
  FiVolumeX,
  FiHash,
  FiFileText,
  FiUsers,
  FiRefreshCw,
  FiTrash2,
  FiCheck,
  FiAlertCircle,
  FiLoader,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { createAuthAxios } from "../../../utils/apiUtils";
import { toast } from "react-toastify";
import "./ContentSettings.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const ContentSettings = () => {
  const [contentPrefs, setContentPrefs] = useState({
    mutedUsers: [],
    hiddenPostsCount: 0,
    notInterestedTopics: [],
    reducedPostTypes: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [clearingHidden, setClearingHidden] = useState(false);

  useEffect(() => {
    fetchContentPreferences();
  }, []);

  // FIXED: Use correct endpoint /users/content-preferences/summary
  const fetchContentPreferences = async () => {
    setIsLoading(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get("/users/content-preferences/summary");

      if (response.data.success) {
        setContentPrefs({
          mutedUsers: response.data.data.mutedUsers || [],
          hiddenPostsCount: response.data.data.hiddenPostsCount || 0,
          notInterestedTopics: response.data.data.notInterestedTopics || [],
          reducedPostTypes: response.data.data.reducedPostTypes || [],
        });
      }
    } catch (error) {
// Don't show error toast on initial load failure - might just be empty prefs
    } finally {
      setIsLoading(false);
    }
  };

  // FIXED: Use correct endpoint DELETE /users/:userId/mute
  const unmuteUser = async (userId, userName) => {
    try {
      const authAxios = createAuthAxios();
      await authAxios.delete(`/users/${userId}/mute`);

      setContentPrefs((prev) => ({
        ...prev,
        mutedUsers: prev.mutedUsers.filter((u) => u._id !== userId),
      }));
      toast.success(`${userName || "User"} unmuted`);
    } catch (error) {
      toast.error("Failed to unmute user");
    }
  };

  // FIXED: Use correct endpoint DELETE /topics/:topic/not-interested
  const restoreTopicToFeed = async (topic) => {
    try {
      const authAxios = createAuthAxios();
      await authAxios.delete(`/topics/${encodeURIComponent(topic)}/not-interested`);

      setContentPrefs((prev) => ({
        ...prev,
        notInterestedTopics: prev.notInterestedTopics.filter((t) => t !== topic),
      }));
      toast.success(`#${topic} restored to your feed`);
    } catch (error) {
      toast.error("Failed to restore topic");
    }
  };

  // FIXED: Use correct endpoint DELETE /posts/reduce-type with data payload
  const restorePostType = async (postType) => {
    try {
      const authAxios = createAuthAxios();
      await authAxios.delete("/posts/reduce-type", { data: { postType } });

      setContentPrefs((prev) => ({
        ...prev,
        reducedPostTypes: prev.reducedPostTypes.filter((t) => t !== postType),
      }));
      toast.success(`${postTypeLabels[postType] || postType} restored to normal visibility`);
    } catch (error) {
      toast.error("Failed to restore post type");
    }
  };

  // NEW: Clear all hidden content
  const clearAllHiddenContent = async () => {
    setClearingHidden(true);
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete("/users/content-preferences/hidden");

      const clearedCount = response.data?.clearedCount || contentPrefs.hiddenPostsCount;
      
      setContentPrefs((prev) => ({
        ...prev,
        hiddenPostsCount: 0,
      }));
      
      toast.success(`Restored ${clearedCount} posts to your feed`);
    } catch (error) {
      toast.error("Failed to clear hidden content");
    } finally {
      setClearingHidden(false);
    }
  };

  const postTypeLabels = {
    text: "Text Posts",
    photo: "Photo Posts",
    question: "Questions",
    research: "Research Posts",
    collaboration: "Collaboration Requests",
    poll: "Polls",
    milestone: "Milestones",
  };

  if (isLoading) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="stg-content-wrapper"
      >
        <div className="stg-loading" style={{ minHeight: "300px" }}>
          <div className="stg-spinner"></div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-cyan">
            <FiSliders />
          </span>
          Content Preferences
        </h1>
        <p className="stg-section-description">
          Manage muted users, hidden content, and topic preferences
        </p>
      </div>

      {/* Muted Users Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiVolumeX /> Muted Users
          </h3>
          <div className="stg-card-actions">
            <button
              className="stg-btn stg-btn-ghost stg-btn-sm"
              onClick={fetchContentPreferences}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </div>
        <div className="stg-card-body">
          {contentPrefs.mutedUsers.length > 0 ? (
            <div className="stg-user-list">
              <AnimatePresence>
                {contentPrefs.mutedUsers.map((user) => (
                  <motion.div
                    key={user._id}
                    className="stg-user-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <Link to={`/profile/${user._id}`} className="stg-user-link">
                      <img
                        src={
                          user.profilePhotoUrl ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user.name || "User"
                          )}&background=6366f1&color=fff&size=40`
                        }
                        alt={user.name}
                        className="stg-user-avatar"
                      />
                      <div className="stg-user-info">
                        <p className="stg-user-name">{user.name}</p>
                        {user.field && (
                          <p className="stg-user-handle">{user.field}</p>
                        )}
                        {user.bio && (
                          <p className="stg-user-bio" style={{ 
                            fontSize: "12px", 
                            color: "var(--stg-slate-500)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px"
                          }}>
                            {user.bio}
                          </p>
                        )}
                      </div>
                    </Link>
                    <div className="stg-user-actions">
                      <button
                        className="stg-btn stg-btn-secondary stg-btn-sm"
                        onClick={() => unmuteUser(user._id, user.name)}
                      >
                        <FiCheck /> Unmute
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="stg-empty">
              <div className="stg-empty-icon">
                <FiUsers />
              </div>
              <h4 className="stg-empty-title">No muted users</h4>
              <p className="stg-empty-text">
                When you mute someone, their content won't appear in your feed
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Hidden Content Card - NEW: Added Clear All button */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiEyeOff /> Hidden Content
          </h3>
        </div>
        <div className="stg-card-body">
          <div className="stg-alert stg-alert-info stg-mb-0" style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <FiAlertCircle className="stg-alert-icon" />
              <div className="stg-alert-content">
                <p className="stg-alert-title">
                  {contentPrefs.hiddenPostsCount} Hidden Posts
                </p>
                <p className="stg-alert-message">
                  You've hidden {contentPrefs.hiddenPostsCount} posts from your
                  feed. These posts won't appear in your home feed anymore.
                </p>
              </div>
            </div>
            {contentPrefs.hiddenPostsCount > 0 && (
              <button
                className="stg-btn stg-btn-danger stg-btn-sm"
                onClick={clearAllHiddenContent}
                disabled={clearingHidden}
                style={{ flexShrink: 0, marginLeft: "16px" }}
              >
                {clearingHidden ? (
                  <>
                    <FiLoader className="stg-spin" /> Clearing...
                  </>
                ) : (
                  <>
                    <FiTrash2 /> Clear All
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Not Interested Topics Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiHash /> Hidden Topics
          </h3>
        </div>
        <div className="stg-card-body">
          {contentPrefs.notInterestedTopics.length > 0 ? (
            <div className="stg-tags">
              <AnimatePresence>
                {contentPrefs.notInterestedTopics.map((topic) => (
                  <motion.span
                    key={topic}
                    className="stg-tag"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                  >
                    #{topic}
                    <button
                      className="stg-tag-remove"
                      onClick={() => restoreTopicToFeed(topic)}
                      title="Restore to feed"
                    >
                      <FiRefreshCw size={12} />
                    </button>
                  </motion.span>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="stg-empty" style={{ padding: "24px" }}>
              <div className="stg-empty-icon">
                <FiHash />
              </div>
              <h4 className="stg-empty-title">No hidden topics</h4>
              <p className="stg-empty-text">
                Topics you mark as "not interested" will appear here
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Reduced Post Types Card */}
      <motion.div variants={itemVariants} className="stg-card">
        <div className="stg-card-header">
          <h3 className="stg-card-title">
            <FiFileText /> Reduced Post Types
          </h3>
        </div>
        <div className="stg-card-body">
          {contentPrefs.reducedPostTypes.length > 0 ? (
            <div className="stg-link-list">
              <AnimatePresence>
                {contentPrefs.reducedPostTypes.map((type) => (
                  <motion.div
                    key={type}
                    className="stg-link-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <div className="stg-link-icon website">
                      <FiFileText />
                    </div>
                    <div className="stg-link-info">
                      <p className="stg-link-platform">
                        {postTypeLabels[type] || type}
                      </p>
                      <p className="stg-link-url">
                        You'll see fewer of these posts
                      </p>
                    </div>
                    <div className="stg-link-actions">
                      <button
                        className="stg-btn stg-btn-secondary stg-btn-sm"
                        onClick={() => restorePostType(type)}
                      >
                        <FiRefreshCw /> Restore
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="stg-empty" style={{ padding: "24px" }}>
              <div className="stg-empty-icon">
                <FiSliders />
              </div>
              <h4 className="stg-empty-title">No reduced post types</h4>
              <p className="stg-empty-text">
                Post types you choose to see less of will appear here
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContentSettings;
